import { useContext, useState } from 'react';

import { AuthContext } from 'contexts/Auth';
import useSelector from 'store/useSelector';
import { getAuthHeader } from 'util/authHeader';

import { Api } from '../../';

export interface Error {
  message?: string;
}
interface InitiateAction {
  status: 'initiate';
  payload?: null;
}

interface CompleteAction<DataType> {
  status: 'complete';
  payload: DataType;
}

interface ErrorAction {
  status: 'error';
  payload: Error;
}

type RequestAction<DataType> = InitiateAction | CompleteAction<DataType> | ErrorAction;
export interface ApiArgs<DataType> {
  onCompleted?: (data: DataType) => void;
  onError?: (error: Error) => void;
  initialLoading?: boolean;
}

interface Config<DataType> {
  activeUserUuid: string;
  referenceID: string;
  data: DataType | null;
  loading: boolean;
  error: Error | null;
  updateRequest: (action: RequestAction<DataType>) => void;
}
type UseApi<DataType> = [Api<unknown>, Config<DataType>];

export default function useApi<DataType>({
  onCompleted,
  onError,
  initialLoading = false,
}: ApiArgs<DataType> = {}): UseApi<DataType> {
  const { auth } = useContext(AuthContext);
  const activeUserUuid = useSelector((app) => app.activeUserUuid);
  const referenceID = useSelector((app) => app.lifeApplicationReferenceId);
  const [data, setData] = useState<Config<DataType>['data']>(null);
  const [loading, setLoading] = useState(initialLoading);
  const [error, setError] = useState<Config<DataType>['error']>(null);

  const api = new Api({
    baseUrl: `${process.env.LIFE_BFF_BASE_URL}/api`,
    baseApiParams: {
      headers: {
        Authorization: getAuthHeader(auth) ?? '',
      },
      credentials: 'include',
    },
  });

  const updateRequest = (action: RequestAction<DataType>) => {
    const { status, payload } = action;

    switch (status) {
      case 'initiate':
        setLoading(true);

        break;
      case 'complete':
        setData(payload);
        setLoading(false);

        onCompleted && onCompleted(payload);

        break;
      case 'error':
        setError(payload);
        setLoading(false);

        onError && onError(payload);

        break;
      default:
        console.warn('Api Request status is not supported');
        break;
    }
  };

  return [
    api,
    {
      activeUserUuid,
      referenceID,
      data,
      loading,
      error,
      updateRequest,
    },
  ];
}
